import { useEffect, useState } from "react";
import Lenis from "lenis";
import { PdpDataProvider } from "@/components/pdp/PdpDataContext";
import MegaHeaderV3 from "@/components/home/MegaHeaderV3";
import RiversolFooter from "@/components/pdp/RiversolFooter";
import HeroSection from "@/components/pdp/HeroSection";
import ProductFacts from "@/components/pdp/ProductFacts";
import SkincareHighlights from "@/components/pdp/SkincareHighlights";
import StickyCartBar from "@/components/pdp/StickyCartBar";
import MarqueeBand from "@/components/pdp/MarqueeBand";
import RiversolInAction from "@/components/pdp/RiversolInAction";
import StoryReset from "@/components/pdp/StoryReset";
import EditorialCollage from "@/components/pdp/EditorialCollage";
import EditorialCollageV33 from "@/components/pdp/EditorialCollageV33";
import StoryDermatology from "@/components/pdp/StoryDermatology";
import ActivesSection from "@/components/pdp/ActivesSection";
import ClinicalSection from "@/components/pdp/ClinicalSection";
import BenefitsWhy from "@/components/pdp/BenefitsWhy";
import WhyRiversolV31 from "@/components/pdp/WhyRiversolV31";
import HowToRitualV31 from "@/components/pdp/HowToRitualV31";
import HinokitiolSection from "@/components/pdp/HinokitiolSection";
import BundleBuilder from "@/components/pdp/BundleBuilder";
import ReviewsSection from "@/components/pdp/ReviewsSection";
import PdpStickyNav from "@/components/pdp/PdpStickyNav";
import ProductStoryV32 from "@/components/pdp/ProductStoryV32";
import { DetailsAccordion, FaqSection } from "@/components/pdp/DetailsAndFaq";
import { CompleteRoutine, FinalConversion } from "@/components/pdp/CompleteRoutine";
import YouMayAlsoLove from "@/components/pdp/YouMayAlsoLove";

export default function PdpLayout({ data }) {
  const [variant, setVariant] = useState(data.defaultVariant);
  const [qty, setQty] = useState(1);

  // Reset scroll on route change so each PDP variant lands at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.1, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <PdpDataProvider data={data}>
      <div
        className={`pdp-scope font-sans antialiased selection:bg-riversol-blue${data.v31 ? " v31" : ""}${data.v32 ? " v32" : ""}${data.v33 ? " v33" : ""}${data.v34 ? " v34" : ""}`}
        style={data.brandDark ? { "--brand-dark": data.brandDark } : undefined}
      >
        <MegaHeaderV3 />
        <main>
          <HeroSection variant={variant} setVariant={setVariant} qty={qty} setQty={setQty} />
          {data.ugc ? (
            <>
              {data.facts && !data.v33 && <ProductFacts />}
              {/* PDP3.4 — "Preventative retinol" story moves directly under the hero. */}
              {data.v34 && <ProductStoryV32 />}
              {/* PDP3.3 reorder — "How to Use" moves directly under the hero (Details). */}
              {data.v33 && <HowToRitualV31 />}
              <RiversolInAction />
              {/* PDP3.3 reorder — trust marquee sits right under Riversol in Action. */}
              {data.v33 && <SkincareHighlights />}
              {/* PDP3.4 — Hinokitiol moves up, right before the Actives (ingredients) section. */}
              {data.v34 && <HinokitiolSection />}
              {/* PDP3.3 reorder — Actives moves above the "Preventative retinol" story. */}
              {data.v33 && <ActivesSection />}
              {/* PDP3.2-only editorial overview, placed right before results. */}
              {data.v32 && !data.v34 && <ProductStoryV32 />}
              <ClinicalSection />
              {!data.v31 && <StoryReset />}
              {/* V3.1 flips the order: Editorial Collage → Dermatologist story.
                  V3 keeps dermatologist story first. V2 shows dermatologist story after. */}
              {data.v31 ? (
                <>
                  {data.v34 ? null : data.v33 ? <EditorialCollageV33 /> : <EditorialCollage />}
                  <StoryDermatology />
                  {/* V3.1-only: signature ingredient beat. Sits directly
                      after Dr. Rivers so the founder story hands off into
                      "meet his signature ingredient", and Actives ("targets
                      the eye area") follows immediately to expand from the
                      hero ingredient into the full active system. */}
                  {!data.v34 && <HinokitiolSection />}
                  {/* V3.2 order below Hinokitiol: Skincare Facts → How to use
                      → Actives. V3.1 keeps only Actives here (its Skincare
                      Facts + ritual render lower, after the collage). */}
                  {data.v32 ? (
                    <>
                      {!data.v33 && <SkincareHighlights />}
                      {!data.v33 && <HowToRitualV31 />}
                      {!data.v33 && <ActivesSection />}
                    </>
                  ) : (
                    <ActivesSection />
                  )}
                </>
              ) : (
                <>
                  {data.v3 && <StoryDermatology />}
                  <EditorialCollage />
                  {!data.v3 && <StoryDermatology />}
                </>
              )}
              {/* Skincare Facts + "How to Use" ritual: V3.1 renders them here
                  (facts, then ritual below). V3.2 already rendered both above
                  under Hinokitiol, so they are skipped here. */}
              {!data.v32 && <SkincareHighlights />}
              {data.v31 && !data.v32 && <HowToRitualV31 />}
              {/* V3.1: comparison table moves ABOVE the details accordion so
                  the "Why Riversol" story lands before the fine-print section. */}
              {data.v31 && data.showBlueComparison !== false && (
                <WhyRiversolV31 titleOverride="Why Riversol Is Different" />
              )}
              {/* V3.1 hides the DetailsAccordion block (Key Benefits /
                  Ingredients / Shipping and Returns) — the ProductFacts
                  scroller, Actives section and Skincare Facts marquee
                  already carry that same information higher up, and the
                  short product description already renders in the hero. */}
              {!data.v31 && <DetailsAccordion />}
              {!data.v34 && <BenefitsWhy showComparison={false} />}
              {!data.v31 && data.showBlueComparison !== false && (
                <BenefitsWhy
                  showBenefits={false}
                  theme="blue"
                  titleOverride={data.v3 ? "Why Riversol Is Different" : "Why Riversol Is Different 2"}
                />
              )}
              {/* V3.1 moves ActivesSection above (right under Hinokitiol);
                  V2/V3 keep the original slot after the light comparison. */}
              {!data.v31 && <ActivesSection />}
              {data.showLightComparison !== false && <BenefitsWhy showBenefits={false} />}
              {/* V3.1: swap CompleteRoutine for the BundleBuilder (adds
                  live milestone rewards + toggle-select bundle deck).
                  V2/V3 keep the classic CompleteRoutine section. */}
              {data.v31 ? <BundleBuilder /> : <CompleteRoutine />}
              <FaqSection />
              <ReviewsSection />
              <YouMayAlsoLove />
            </>
          ) : (
            <>
              <MarqueeBand />
              <StoryReset />
              <EditorialCollage />
              <StoryDermatology />
              <ActivesSection />
              <ClinicalSection />
              <BenefitsWhy />
              <ReviewsSection />
              <DetailsAccordion />
              <FinalConversion variant={variant} />
              <FaqSection />
              <CompleteRoutine />
            </>
          )}
        </main>
        <RiversolFooter hp3={!!data.v34} />
        {/* V3.2 swaps the bottom cart bar for a TOP sticky sub-nav that
            appears once the hero/purchase area is scrolled past. */}
        {data.v32 ? <PdpStickyNav variant={variant} /> : <StickyCartBar variant={variant} />}
      </div>
    </PdpDataProvider>
  );
}
